import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import { TripLegType } from '../../../data/models/reservation.model';

import reservationUtils from '../../../services/reservationUtils';

import * as clientSlice from '../../../data/client.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as driverSlice from '../../../data/slices/driver.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as reservationSlice from '../../../data/reservation.slice';

import TripLegDialog from './TripLegDialog';
import ReservationDetailsHeader from './ReservationDetailsHeader';
import ReservationDetails from './ReservationDetails';
import { EditTripLegDialog } from '@/components/reservations/EditTripLegDialog';
import {
  useFeatureEditReservationClientPortal,
  useFeatureEditReservationF1,
} from '@/hooks/features/useFeatureEditReservation';
import { usePortalContext } from '@/contexts/PortalProvider';
import { useUserClientBC } from '@/hooks/useUserClientBC';

const PREFIX = 'ReservationDetailsPage';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const ReservationDetailsPage = () => {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const { isClientPortal } = usePortalContext();
  const clientBC = useUserClientBC();
  const editReservationRefactor =
    (!isClientPortal && useFeatureEditReservationF1()) ||
    (isClientPortal && useFeatureEditReservationClientPortal());

  const { passengerId, referenceId } = useParams<{
    passengerId: string;
    referenceId: string;
  }>();

  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const loadingPassenger = useAppSelector(
    passengerSlice.selectLoadingCurrentPassenger,
  );
  const pastReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerPastReservations,
  );
  const futureReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerFutureReservations,
  );
  const clientData = useAppSelector(clientSlice.selectPassengerClientData);
  const tripLegEvent = useAppSelector(tripLegSlice.selectTripLegEventEmitted);
  const fallbackReservation = useAppSelector(
    passengerSlice.selectCurrentPassengerReservationLookup,
  );
  const loadingReservation = useAppSelector(
    passengerSlice.selectLoadingCurrentPassengerReservationLookup,
  );
  const loadedReservationLookup = useAppSelector(
    passengerSlice.selectLoadedCurrentPassengerReservationLookup,
  );

  const [legReferenceID, setLegReferenceID] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditTripLegDialogOpen, setIsEditTripLegDialogOpen] = useState(false);
  const reservationFromStore = useMemo(() => {
    const fromPast = pastReservations.find(
      (res) => res.reservationReferenceID === referenceId,
    );
    const fromFuture = futureReservations.find(
      (res) => res.reservationReferenceID === referenceId,
    );

    if (!fromPast && !fromFuture) return undefined;
    if (fromPast && !fromFuture) return fromPast;
    if (!fromPast && fromFuture) return fromFuture;

    // Merge trip legs from both lists to include all legs (e.g. active + canceled)
    const futureLegIDs = new Set(
      fromFuture!.tripLegs.map((l) => l.tripLegReferenceID),
    );
    const pastOnlyLegs = fromPast!.tripLegs.filter(
      (l) => !futureLegIDs.has(l.tripLegReferenceID),
    );
    const mergedLegs = [...pastOnlyLegs, ...fromFuture!.tripLegs].sort(
      (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
    );
    return { ...fromFuture!, tripLegs: mergedLegs };
  }, [pastReservations, futureReservations, referenceId]);

  const reservation = useMemo(() => {
    const base = fallbackReservation ?? reservationFromStore;
    if (!base) return undefined;

    const other = fallbackReservation ? reservationFromStore : undefined;
    if (!other) return base;

    const baseIds = new Set(base.tripLegs.map((l) => l.tripLegReferenceID));
    const extraLegs = other.tripLegs.filter(
      (l) => !baseIds.has(l.tripLegReferenceID),
    );
    if (extraLegs.length === 0) return base;

    const mergedLegs = [...extraLegs, ...base.tripLegs].sort(
      (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
    );
    return { ...base, tripLegs: mergedLegs };
  }, [fallbackReservation, reservationFromStore]);

  useEffect(() => {
    if (!reservation?.tripLegs?.length) return;

    const currentLegExists = reservation.tripLegs.some(
      (tl) => tl.tripLegReferenceID === legReferenceID,
    );

    if (!legReferenceID || !currentLegExists) {
      const sortedTripLegs = reservationUtils.getSortedTripLegs(
        reservation.tripLegs,
      );
      setLegReferenceID(sortedTripLegs[0].tripLegReferenceID!);
    }
  }, [legReferenceID, reservation?.tripLegs]);

  const tripLeg = useMemo(() => {
    const leg = reservation?.tripLegs?.find(
      (tl) => tl.tripLegReferenceID === legReferenceID,
    );

    return !!leg
      ? {
          ...leg,
          reservation: {
            reservationReferenceID: reservation?.reservationReferenceID,
            standingOrderUUID: reservation?.standingOrderUUID,
          },
        }
      : null;
  }, [reservation, legReferenceID]);

  const isRoundTrip = useMemo(
    () => (reservation ? reservationUtils.isRoundTrip(reservation) : false),
    [reservation],
  );

  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);

  const provider = useAppSelector((state) =>
    tripLeg?.transportationProviderID
      ? providerSlice.selectProviderByID(
          state,
          tripLeg.transportationProviderID,
        )
      : undefined,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const handleEdit = () => {
    if (editReservationRefactor) {
      setIsEditTripLegDialogOpen(true);
    } else {
      setIsEditDialogOpen(true);
    }
  };

  useEffect(() => {
    const tripLegID = searchParams.get('tripLegID');
    if (!tripLegID) return;

    const leg = reservation?.tripLegs?.find(
      (tl) => tl.tripLegReferenceID === tripLegID,
    );
    if (!!leg) {
      setLegReferenceID(tripLegID);
    } else if (
      passengerId &&
      referenceId &&
      passenger?.passengerUUID &&
      fallbackReservation !== null &&
      fallbackReservation !== undefined
    ) {
      dispatch(
        passengerSlice.fetchMissingTripLegIntoReservationLookupAsync(
          passenger.passengerUUID,
          referenceId,
          tripLegID,
        ),
      );
    }
  }, [
    searchParams,
    reservation,
    fallbackReservation,
    passenger,
    passengerId,
    referenceId,
    dispatch,
  ]);

  useEffect(() => {
    if (tripLeg && syncedTripLegs) {
      const syncedTripLeg = syncedTripLegs[tripLeg.tripLegReferenceID!];
      const driverId = syncedTripLeg?.driverID;
      const isTripLegInProgress = reservationUtils.isTripLegInProgress(tripLeg);

      if (driverId && isTripLegInProgress) {
        dispatch(driverSlice.fetchDriverLocationAsync(driverId));
      }
    }
  }, [tripLeg, syncedTripLegs, dispatch]);

  useEffect(() => {
    dispatch(tripLegSlice.clearTripLegDetails());
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  useEffect(() => {
    reservation?.tripLegs.forEach((tl) => {
      dispatch(tripLegSlice.getTripLegDetailsAsync(tl.tripLegReferenceID!));
    });
    if (
      tripLeg?.transportationProviderID &&
      provider?.transportationProviderID !== tripLeg?.transportationProviderID
    ) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg?.transportationProviderID!),
      );
    }
  }, [reservation, tripLeg?.transportationProviderID]);

  useEffect(() => {
    if (passengerId) {
      dispatch(passengerSlice.fetchPassengerAsync(passengerId));
    }
  }, [dispatch, passengerId]);

  useEffect(() => {
    dispatch(passengerSlice.clearCurrentPassengerReservationLookup());
  }, [dispatch, passengerId, referenceId]);

  useEffect(() => {
    if (
      !passengerId ||
      !referenceId ||
      loadingReservation ||
      loadedReservationLookup
    ) {
      return;
    }
    dispatch(
      passengerSlice.fetchPassengerReservationByReferenceAsync(
        passengerId,
        referenceId,
        clientBC,
        t('common.reservationDetailsLoadError'),
      ),
    );
  }, [
    dispatch,
    passengerId,
    referenceId,
    loadingReservation,
    loadedReservationLookup,
    clientBC,
    t,
  ]);

  // Saving a leg only patches the future reservations list, but the page
  // renders the lookup copy. Refetch it so edits (e.g. notes) show right away.
  const savedTripLeg = useAppSelector(reservationSlice.selectSavedTripLeg);
  const prevSavedTripLegRef = useRef(savedTripLeg);
  useEffect(() => {
    const justSaved = savedTripLeg && !prevSavedTripLegRef.current;
    prevSavedTripLegRef.current = savedTripLeg;
    if (!justSaved || !passengerId || !referenceId) return;
    dispatch(
      passengerSlice.fetchPassengerReservationByReferenceAsync(
        passengerId,
        referenceId,
        clientBC,
        t('common.reservationDetailsLoadError'),
      ),
    );
  }, [savedTripLeg, passengerId, referenceId, clientBC, dispatch, t]);

  useEffect(() => {
    if (passenger) {
      dispatch(
        clientSlice.fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
    }
  }, [passenger]);

  useEffect(() => {
    if (tripLegEvent) {
      const updatedLegId = tripLegEvent.detail.tripLegReferenceID;
      const matchesCurrent = reservation?.tripLegs.some(
        (tl) => tl.tripLegReferenceID === updatedLegId,
      );
      if (matchesCurrent && passengerId && referenceId) {
        if (updatedLegId) {
          dispatch(tripLegSlice.getTripLegDetailsAsync(updatedLegId));
        }
        dispatch(
          passengerSlice.fetchPassengerReservationByReferenceAsync(
            passengerId,
            referenceId,
            clientBC,
            t('common.reservationDetailsLoadError'),
          ),
        );
      }
      dispatch(tripLegSlice.setTripLegEventEmitted(null));
    }
  }, [
    tripLegEvent,
    reservation,
    passengerId,
    referenceId,
    dispatch,
    clientBC,
    t,
  ]);

  return (
    <StyledBox className={classes.container}>
      <ReservationDetailsHeader
        passenger={passenger}
        onEdit={handleEdit}
        editDisabled={!tripLeg}
      />
      {tripLeg && (
        <ReservationDetails
          passenger={passenger}
          reservation={reservation}
          syncedTripLegs={syncedTripLegs || null}
          legReferenceID={legReferenceID}
          setLegReferenceID={setLegReferenceID}
        />
      )}
      {/* Mounted on open: each dialog builds its form and dispatches lookup
          and trip leg fetches as soon as it mounts. */}
      {tripLeg &&
        passenger &&
        referenceId &&
        !editReservationRefactor &&
        isEditDialogOpen && (
          <TripLegDialog
            sequence={tripLeg.tripLegSequence}
            reservationReferenceID={referenceId}
            tripLeg={tripLeg}
            tripType={TripLegType.MultiLeg}
            open={isEditDialogOpen}
            passenger={passenger}
            servicePlans={clientData.servicePlans}
            close={() => setIsEditDialogOpen(false)}
          />
        )}
      {tripLeg &&
        passenger &&
        referenceId &&
        editReservationRefactor &&
        isEditTripLegDialogOpen && (
          <EditTripLegDialog
            sequence={tripLeg.tripLegSequence}
            reservationReferenceID={referenceId}
            tripLeg={tripLeg}
            tripType={TripLegType.MultiLeg}
            open={isEditTripLegDialogOpen}
            passenger={passenger}
            servicePlans={clientData.servicePlans}
            onClose={() => setIsEditTripLegDialogOpen(false)}
          />
        )}
    </StyledBox>
  );
};

export default ReservationDetailsPage;
